// import { type AppId } from './BottomNav';
import { Sun, Moon, ChevronsUpDown, Check, FolderPlus, Plus } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { useState } from 'react';
import type { Project } from '@/types/projects';
import { useFullscreen } from '@/hooks/useFullscreen';
import { Maximize, Minimize } from 'lucide-react';



interface TopBarProps {
  projects: Project[];
  activeProject: Project | null;
  onSelectProject: (project: Project) => void;
  onCreateProject: (name: string) => void;
  userInitials: string;
  theme: 'dark' | 'light';
  onThemeToggle: () => void;
}
// const APP_LABELS: Record<AppId, string> = {
//   'drillholes':         'Drillholes',
//   'surface-mapping':    'Surface Mapping',
//   'lab-testing':        'Lab Testing',
//   'geotechnical-model': 'Geotechnical Model',
//   'ground-support':     'Ground Support',
// };

export function TopBar({
  projects,
  activeProject,
  onSelectProject,
  onCreateProject,
  userInitials,
  theme,
  onThemeToggle,
}: TopBarProps) {
  const [dialogOpen, setDialogOpen]         = useState(false);
  const [newProjectName, setNewProjectName] = useState('');

  const { isFullscreen, toggle: toggleFullscreen } = useFullscreen();

  const handleCreate = () => {
    if (!newProjectName.trim()) return;
    onCreateProject(newProjectName.trim());
    setNewProjectName('');
    setDialogOpen(false);
  };

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-40 flex h-12 items-center justify-between border-b border-border bg-background px-4 md:hidden">

        {/* Project switcher */}
        {projects.length === 0 ? (
          <button
            onClick={() => setDialogOpen(true)}
            className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <div className="w-5 h-5 rounded border border-dashed border-border flex items-center justify-center">
              <Plus size={10} />
            </div>
            <span>No projects</span>
          </button>
        ) : (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
                <div className="w-5 h-5 rounded bg-primary flex items-center justify-center text-[9px] font-semibold text-primary-foreground">
                  {activeProject?.name[0].toUpperCase() ?? 'P'}
                </div>
                <span>{activeProject?.name ?? 'Select project'}</span>
                <ChevronsUpDown size={11} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent side="bottom" align="start" className="w-[200px]">
              <DropdownMenuLabel className="text-xs text-muted-foreground">Projects</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {projects.map((p) => (
                <DropdownMenuItem
                  key={p.id}
                  onClick={() => onSelectProject(p)}
                  className="flex items-center justify-between"
                >
                  <span>{p.name}</span>
                  {p.id === activeProject?.id && <Check size={12} className="text-muted-foreground" />}
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => setDialogOpen(true)}
                className="flex items-center gap-2 text-muted-foreground"
              >
                <FolderPlus size={13} />
                <span>Create project</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        <div className="flex items-center gap-2">
          <button
            onClick={toggleFullscreen}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            {isFullscreen ? <Minimize size={16} /> : <Maximize size={16} />}
          </button>
          <button
            onClick={onThemeToggle}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
          </button>
          <div className="w-7 h-7 rounded-full bg-muted flex items-center justify-center text-[10px] font-semibold">
            {userInitials}
          </div>
        </div>
      </header>

      {/* Create project dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Create project</DialogTitle>
            <DialogDescription>
              Add a new project to your workspace.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-3 py-2">
            <Label htmlFor="project-name-mobile">Project name</Label>
            <Input
              id="project-name-mobile"
              placeholder="e.g. GMT Fase 4"
              value={newProjectName}
              onChange={(e) => setNewProjectName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={!newProjectName.trim()}>Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}